import React from 'react';
import '@fontsource/outfit/400.css';
import '@fontsource/outfit/500.css';
import '@fontsource/outfit/600.css';
import '@fontsource/outfit/700.css';
import '@fontsource/manrope/400.css';
import '@fontsource/manrope/500.css';
import '@fontsource/manrope/600.css';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { LanguageProvider } from "@/context/LanguageContext";

// Pages
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import Dashboard from "@/pages/Dashboard";
import ImageToPrompt from "@/pages/ImageToPrompt";
import PromptEnhancer from "@/pages/PromptEnhancer";
import ImageGenerator from "@/pages/ImageGenerator";
import MockupGenerator from "@/pages/MockupGenerator";
import BackgroundGenerator from "@/pages/BackgroundGenerator";
import WorkflowPage from "@/pages/WorkflowPage";
import PricingPage from "@/pages/PricingPage";
import SettingsPage from "@/pages/SettingsPage";
import SharePage from "@/pages/SharePage";
import GalleryPage from "@/pages/GalleryPage";
import FAQPage from "@/pages/FAQPage";
import ContactPage from "@/pages/ContactPage";
import LegalPages from "@/pages/LegalPages";
import HowCreditsWorkPage from "@/pages/HowCreditsWorkPage";
import AdminDashboard from "@/pages/AdminDashboard";
import DashboardLayout from "@/components/DashboardLayout";

// Signup redirect - handles referral links from mockuppro.org/signup?ref=CODE
const SignupRedirect = () => {
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref');
  
  // Redirect to auth page with register mode and referral code
  const redirectUrl = refCode 
    ? `/auth?mode=register&ref=${refCode}`
    : '/auth?mode=register';
  
  return <Navigate to={redirectUrl} replace />;
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<PublicRoute><AuthPage /></PublicRoute>} />
      {/* Signup redirect - handles referral links */}
      <Route path="/signup" element={<SignupRedirect />} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/share/:shareId" element={<SharePage />} />
      
      {/* Legal Pages */}
      <Route path="/terms" element={<LegalPages />} />
      <Route path="/privacy" element={<LegalPages />} />
      <Route path="/refund-policy" element={<LegalPages />} />
      <Route path="/acceptable-use" element={<LegalPages />} />
      
      {/* Info Pages */}
      <Route path="/how-credits-work" element={<HowCreditsWorkPage />} />
      
      {/* Protected Dashboard Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="image-to-prompt" element={<ImageToPrompt />} />
        <Route path="prompt-enhancer" element={<PromptEnhancer />} />
        <Route path="image-generator" element={<ImageGenerator />} />
        <Route path="generate" element={<ImageGenerator />} />
        <Route path="mockup-generator" element={<MockupGenerator />} />
        <Route path="mockup" element={<MockupGenerator />} />
        <Route path="background-generator" element={<BackgroundGenerator />} />
        <Route path="background" element={<BackgroundGenerator />} />
        <Route path="workflow" element={<WorkflowPage />} />
        <Route path="gallery" element={<GalleryPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      
      {/* Admin Dashboard - Protected, admin-only */}
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <div className="App">
              <AppRoutes />
              <Toaster position="bottom-right" />
            </div>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
